import React, { useState, useEffect } from 'react';
import { Grid3x3, Save, Loader2, AlertTriangle, Star, TrendingDown } from 'lucide-react';
import api from '../../services/api';

const BADGES = [
  '', 'Découverte', 'Flexible', 'Confort', 'Populaire', 'Meilleur choix',
  'Offre famille', 'Premium', 'Prix le plus bas / mois', 'Meilleur rapport qualité-prix'
];

const PricingMatrix = () => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settings/pricing-matrix');
        setMatrix(res.data);
      } catch {
        setMsg({ type: 'error', text: 'Erreur de chargement de la grille' });
      } finally { setLoading(false); }
    })();
  }, []);

  const cfg = matrix?.config || { credit_cost: 15, min_coefficient: 2, margin_threshold: 50 };
  const creditsOf = (dkey) => {
    const d = matrix?.durations.find(x => x.key === dkey);
    return d ? (d.credits || 0) : 0;
  };
  const monthsOf = (dkey) => {
    const d = matrix?.durations.find(x => x.key === dkey);
    return d ? (d.months || 1) : 1;
  };

  const computeCell = (dkey, s, cell) => {
    const cost = creditsOf(dkey) * (parseFloat(cfg.credit_cost) || 0) * s;
    const minPrice = cost * (parseFloat(cfg.min_coefficient) || 0);
    const price = parseFloat(cell.price) || 0;
    const marginEur = price > 0 ? price - cost : null;
    const marginPct = price > 0 ? ((price - cost) / price) * 100 : null;
    const monthly = price > 0 ? price / monthsOf(dkey) : null;
    const belowMin = cell.active && price > 0 && price < minPrice;
    const lossSelling = marginEur !== null && marginEur < 0;
    const lowMargin = marginPct !== null && !lossSelling && marginPct < (parseFloat(cfg.margin_threshold) || 0);
    return { cost, minPrice, marginEur, marginPct, monthly, belowMin, lossSelling, lowMargin };
  };

  const updateCell = (dkey, s, field, value) => {
    setMatrix(prev => {
      const prices = { ...prev.prices };
      const row = { ...(prices[dkey] || {}) };
      const cell = { ...(row[s] || { price: 0, active: false }) };
      if (field === 'price' || field === 'strike_price') cell[field] = value === '' ? '' : (parseFloat(value) || 0);
      else cell[field] = value;
      row[s] = cell; prices[dkey] = row;
      return { ...prev, prices };
    });
  };

  const updateConfig = (field, value) => {
    setMatrix(prev => ({ ...prev, config: { ...prev.config, [field]: value === '' ? '' : (parseFloat(value) || 0) } }));
  };

  const toggleRow = (dkey, active) => {
    setMatrix(prev => {
      const prices = { ...prev.prices };
      const row = { ...(prices[dkey] || {}) };
      for (let s = 1; s <= prev.max_screens; s++) row[s] = { ...(row[s] || { price: 0 }), active };
      prices[dkey] = row; return { ...prev, prices };
    });
  };

  const handleSave = async () => {
    // Validation prix minimum côté client (blocante)
    const violations = [];
    matrix.durations.forEach(d => {
      for (let s = 1; s <= matrix.max_screens; s++) {
        const cell = (matrix.prices[d.key] && matrix.prices[d.key][s]) || {};
        const c = computeCell(d.key, s, cell);
        if (c.belowMin) violations.push(`${d.label} · ${s} écran(s) : ${cell.price}€ < min ${c.minPrice.toFixed(2)}€`);
      }
    });
    if (violations.length) {
      setMsg({ type: 'error', text: 'Prix trop bas (sous le seuil de rentabilité) : ' + violations.join(' | ') });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const res = await api.post('/admin/settings/pricing-matrix', matrix);
      setMatrix(prev => ({ ...prev, config: res.data.config || prev.config }));
      setMsg({ type: 'success', text: 'Grille tarifaire sauvegardée' });
    } catch (e) {
      const d = e.response?.data?.detail;
      const text = (d && typeof d === 'object') ? (d.message + (d.violations ? ' — ' + d.violations.map(v => v.message).join(' | ') : '')) : (d || 'Erreur');
      setMsg({ type: 'error', text });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(m => (m && m.type === 'success' ? null : m)), 4000);
    }
  };

  if (loading) return <div className="text-[#B8B8BA] text-center py-12">Chargement de la grille...</div>;
  if (!matrix) return null;

  const screens = Array.from({ length: matrix.max_screens }, (_, i) => i + 1);
  const eur = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}€`);

  return (
    <div className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]" data-testid="pricing-matrix">
      <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
        <Grid3x3 className="h-5 w-5 text-[#00FFFF]" /> Grille tarifaire — pilotage commercial
      </h2>
      <p className="text-[#B8B8BA] text-sm mb-4">
        Saisissez vos prix de vente. Le coût fournisseur, la marge et le prix mensuel sont calculés automatiquement.
      </p>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg border text-sm ${msg.type === 'success' ? 'bg-[#00FF88]/10 border-[#00FF88] text-[#00FF88]' : 'bg-[#FF3B5C]/10 border-[#FF3B5C] text-[#FF3B5C]'}`} data-testid="matrix-message">
          {msg.text}
        </div>
      )}

      {/* Config coûts/marges */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 bg-[#222226] p-4 rounded-lg">
        <div>
          <label className="block text-[#B8B8BA] text-xs mb-1">Coût d'1 crédit (€)</label>
          <input type="number" step="0.01" value={cfg.credit_cost} onChange={e => updateConfig('credit_cost', e.target.value)}
            className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white focus:border-[#00FFFF] focus:outline-none" data-testid="cfg-credit-cost" />
        </div>
        <div>
          <label className="block text-[#B8B8BA] text-xs mb-1">Coefficient prix minimum (×coût)</label>
          <input type="number" step="0.1" value={cfg.min_coefficient} onChange={e => updateConfig('min_coefficient', e.target.value)}
            className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white focus:border-[#00FFFF] focus:outline-none" data-testid="cfg-min-coef" />
        </div>
        <div>
          <label className="block text-[#B8B8BA] text-xs mb-1">Seuil marge faible (%)</label>
          <input type="number" step="1" value={cfg.margin_threshold} onChange={e => updateConfig('margin_threshold', e.target.value)}
            className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white focus:border-[#00FFFF] focus:outline-none" data-testid="cfg-margin-threshold" />
        </div>
      </div>

      {/* Sections par durée */}
      <div className="space-y-6">
        {matrix.durations.map(d => (
          <div key={d.key} className="border border-[#3A3A3D] rounded-lg overflow-hidden" data-testid={`matrix-row-${d.key}`}>
            <div className="flex items-center justify-between bg-[#222226] px-4 py-2">
              <span className="text-white font-semibold">{d.label} <span className="text-[#6B6B6E] text-xs">({d.credits} crédit/écran · {d.months} mois)</span></span>
              <div className="flex gap-3 text-[10px]">
                <button onClick={() => toggleRow(d.key, true)} className="text-[#6B6B6E] hover:text-[#00FF88]" data-testid={`matrix-activate-row-${d.key}`}>tout activer</button>
                <button onClick={() => toggleRow(d.key, false)} className="text-[#6B6B6E] hover:text-[#FF3B5C]">tout désactiver</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#6B6B6E]">
                    <th className="p-2 text-left">Écrans</th>
                    <th className="p-2">Prix vente €</th>
                    <th className="p-2">Prix barré €</th>
                    <th className="p-2">Badge</th>
                    <th className="p-2">À la une</th>
                    <th className="p-2">Actif</th>
                    <th className="p-2">Coût</th>
                    <th className="p-2">Marge €</th>
                    <th className="p-2">Marge %</th>
                    <th className="p-2">€/mois</th>
                    <th className="p-2 text-left">Alerte</th>
                  </tr>
                </thead>
                <tbody>
                  {screens.map(s => {
                    const cell = (matrix.prices[d.key] && matrix.prices[d.key][s]) || { price: 0, active: false };
                    const c = computeCell(d.key, s, cell);
                    return (
                      <tr key={s} className="border-t border-[#3A3A3D]">
                        <td className="p-2 text-white font-semibold">{s}</td>
                        <td className="p-2">
                          <input type="number" min="0" step="0.01" value={cell.price ?? ''} onChange={e => updateCell(d.key, s, 'price', e.target.value)}
                            className={`w-20 bg-[#1A1A1D] border rounded px-2 py-1 text-white text-center focus:outline-none ${c.belowMin ? 'border-[#FF3B5C]' : 'border-[#3A3A3D] focus:border-[#00FFFF]'}`}
                            data-testid={`matrix-price-${d.key}-${s}`} />
                        </td>
                        <td className="p-2">
                          <input type="number" min="0" step="0.01" value={cell.strike_price ?? ''} onChange={e => updateCell(d.key, s, 'strike_price', e.target.value)}
                            placeholder="—"
                            className="w-20 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-2 py-1 text-[#B8B8BA] text-center focus:border-[#00FFFF] focus:outline-none"
                            data-testid={`matrix-strike-${d.key}-${s}`} />
                        </td>
                        <td className="p-2">
                          <select value={cell.badge || ''} onChange={e => updateCell(d.key, s, 'badge', e.target.value)}
                            className="bg-[#1A1A1D] border border-[#3A3A3D] rounded px-1 py-1 text-white focus:border-[#00FFFF] focus:outline-none max-w-[130px]"
                            data-testid={`matrix-badge-${d.key}-${s}`}>
                            {BADGES.map(b => <option key={b} value={b}>{b || '—'}</option>)}
                          </select>
                        </td>
                        <td className="p-2 text-center">
                          <button onClick={() => updateCell(d.key, s, 'featured', !cell.featured)} data-testid={`matrix-featured-${d.key}-${s}`}>
                            <Star className={`h-4 w-4 mx-auto ${cell.featured ? 'text-[#FFB800] fill-[#FFB800]' : 'text-[#6B6B6E]'}`} />
                          </button>
                        </td>
                        <td className="p-2 text-center">
                          <input type="checkbox" checked={!!cell.active} onChange={e => updateCell(d.key, s, 'active', e.target.checked)}
                            className="accent-[#00FF88] h-4 w-4" data-testid={`matrix-active-${d.key}-${s}`} />
                        </td>
                        <td className="p-2 text-center text-[#B8B8BA]">{eur(c.cost)}</td>
                        <td className={`p-2 text-center font-semibold ${c.marginEur === null ? 'text-[#6B6B6E]' : c.marginEur < 0 ? 'text-[#FF3B5C]' : 'text-[#00FF88]'}`}>{c.marginEur === null ? 'Non défini' : eur(c.marginEur)}</td>
                        <td className={`p-2 text-center ${c.marginPct === null ? 'text-[#6B6B6E]' : c.marginPct < 0 ? 'text-[#FF3B5C]' : c.lowMargin ? 'text-[#FFB800]' : 'text-[#00FF88]'}`}>{c.marginPct === null ? '—' : `${c.marginPct.toFixed(0)}%`}</td>
                        <td className="p-2 text-center text-[#00FFFF]">{c.monthly === null ? '—' : `${c.monthly.toFixed(2)}`}</td>
                        <td className="p-2">
                          {c.belowMin && <span className="text-[#FF3B5C] flex items-center gap-1" data-testid={`alert-belowmin-${d.key}-${s}`}><AlertTriangle className="h-3 w-3" />Prix trop bas (min {c.minPrice.toFixed(0)}€)</span>}
                          {!c.belowMin && c.lossSelling && <span className="text-[#FF3B5C] flex items-center gap-1"><TrendingDown className="h-3 w-3" />Vente à perte</span>}
                          {!c.belowMin && !c.lossSelling && c.lowMargin && <span className="text-[#FFB800] flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Marge faible</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <button onClick={handleSave} disabled={saving}
        className="mt-6 flex items-center gap-2 px-6 py-2.5 bg-[#00FFFF] text-black font-bold rounded-lg hover:bg-[#00E5E5] transition-colors disabled:opacity-50 text-sm"
        data-testid="matrix-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Sauvegarder la grille
      </button>
    </div>
  );
};

export default PricingMatrix;
